<template>
	<k-dialog v-model="dialog_open" max-width="900" persistent scrollable aria-labelledby="satchel-custom-export-title" @keydown.esc="on_cancel">
		<k-card>
			<k-card-title class="k-custom-export-title">
				<b id="satchel-custom-export-title">Generate Custom Export</b>
				<k-spacer></k-spacer>
				<k-btn small color="secondary" @click="on_cancel" class="ml-4 mr-1"><k-icon small class="mr-2">fas fa-times</k-icon>Done</k-btn>
			</k-card-title>
			<k-card-text class="k-custom-export-body pt-6 px-6 pb-4">
				<div class="mb-4">
					<k-select
						v-model="selected_export_id"
						:items="export_options"
						label="Export type"
						outlined dense hide-details
					></k-select>
				</div>

				<div class="mb-3">
					<k-autocomplete
						v-model="selected_categories"
						:items="category_options"
						label="Framework categories"
						multiple chips deletable-chips outlined dense hide-details
					></k-autocomplete>
				</div>

				<div v-if="selected_categories.length" class="k-custom-export-categories">
					<div v-for="category in selected_categories" :key="category" class="k-custom-export-category mb-3 pa-3 rounded">
						<div class="d-flex align-center mb-2">
							<b>{{category_label(category)}}</b>
							<k-spacer/>
							<k-btn x-small text @click="set_all_in_category(category, true)">All</k-btn>
							<k-btn x-small text @click="set_all_in_category(category, false)">None</k-btn>
						</div>
						<div v-for="fr in frameworks_in_category(category)" :key="fr.lsdoc_identifier">
							<k-checkbox
								:value="is_framework_selected(fr.lsdoc_identifier)"
								:label="fr.json.CFDocument.title"
								hide-details dense class="mt-0 pt-0"
								@change="set_framework_selected(fr.lsdoc_identifier, $event)"
							></k-checkbox>
						</div>
					</div>
				</div>

				<div v-if="selected_framework_identifiers.length > 0" class="mt-4 d-flex">
					<k-spacer/>
					<k-btn
						color="primary"
						:disabled="!can_export || exporting"
						@click="export_data"
					>
						<k-icon small class="mr-2">fas fa-file-arrow-down</k-icon>
						<span v-if="!exporting">Export Data</span>
						<span v-else>Exporting…</span>
					</k-btn>
				</div>
			</k-card-text>
		</k-card>
	</k-dialog>
</template>

<script>
import { mapState } from 'vuex'

const NOT_CATEGORIZED = '__not_categorized__'
const MAX_FRAMEWORKS = 200

export default {
	data() { return {
		dialog_open: true,
		selected_export_id: '',
		selected_categories: [],
		selected_framework_identifiers: [],
		exporting: false,
		auto_selected_categories: new Set(),
		over_cap_warning_shown: false,
	}},

	computed: {
		...mapState(['framework_records', 'site_config']),

		custom_exports() {
			return this.site_config?.custom_exports || {}
		},

		export_options() {
			return Object.entries(this.custom_exports).map(([id, def]) => ({ value: id, text: def.title || id }))
		},

		exportable_framework_records() {
			return this.framework_records.filter(fr => fr.json?.CFDocument?.frameworkType !== 'crosswalk')
		},

		category_options() {
			const categories = new Set()
			let has_uncategorized = false
			for (const fr of this.exportable_framework_records) {
				const category = fr.ss_framework_data?.category || ''
				if (category) categories.add(category)
				else has_uncategorized = true
			}
			// sort by raw string so the "01 Georgia" sequence prefix orders correctly; display only the parsed title
			const sorted_categories = Array.from(categories).sort(U.natural_sort)
			const options = sorted_categories.map(category => ({ value: category, text: U.parse_framework_category(category).title }))
			if (has_uncategorized) options.push({ value: NOT_CATEGORIZED, text: '(not categorized)' })
			return options
		},

		can_export() {
			const count = this.selected_framework_identifiers.length
			return !!this.selected_export_id && count > 0 && count <= MAX_FRAMEWORKS
		},
	},

	watch: {
		selected_framework_identifiers(newer) {
			if (newer.length > MAX_FRAMEWORKS) {
				if (!this.over_cap_warning_shown) {
					this.over_cap_warning_shown = true
					vapp.$alert(`You’ve selected more than ${MAX_FRAMEWORKS} frameworks. Please reduce your selection to ${MAX_FRAMEWORKS} or fewer to export.`)
				}
			} else {
				this.over_cap_warning_shown = false
			}
		},

		// reconcile against an internal Set rather than the watcher's `older` arg, which v-select can mutate in place
		selected_categories(newer) {
			for (const category of newer) {
				if (!this.auto_selected_categories.has(category)) {
					this.auto_selected_categories.add(category)
					for (const fr of this.frameworks_in_category(category)) {
						if (!this.selected_framework_identifiers.includes(fr.lsdoc_identifier)) {
							this.selected_framework_identifiers.push(fr.lsdoc_identifier)
						}
					}
				}
			}
			const still_present = new Set(newer)
			for (const category of Array.from(this.auto_selected_categories)) {
				if (!still_present.has(category)) {
					this.auto_selected_categories.delete(category)
					const framework_ids = this.frameworks_in_category(category).map(fr => fr.lsdoc_identifier)
					this.selected_framework_identifiers = this.selected_framework_identifiers.filter(id => !framework_ids.includes(id))
				}
			}
		},
	},

	created() {
		const first_option = this.export_options[0]
		if (first_option) this.selected_export_id = first_option.value
	},

	methods: {
		on_cancel() {
			this.$emit('dialog_cancel')
		},

		is_framework_selected(fid) {
			return this.selected_framework_identifiers.includes(fid)
		},
		set_framework_selected(fid, checked) {
			const is_already_selected = this.selected_framework_identifiers.includes(fid)
			if (checked && !is_already_selected) {
				this.selected_framework_identifiers.push(fid)
			} else if (!checked && is_already_selected) {
				this.selected_framework_identifiers = this.selected_framework_identifiers.filter(id => id !== fid)
			}
		},

		category_label(category) {
			if (category === NOT_CATEGORIZED) return '(not categorized)'
			return U.parse_framework_category(category).title
		},

		frameworks_in_category(category) {
			const target_category = (category === NOT_CATEGORIZED) ? '' : category
			return this.exportable_framework_records
				.filter(fr => (fr.ss_framework_data?.category || '') === target_category)
				.sort((a, b) => U.natural_sort(a.json.CFDocument.title || '', b.json.CFDocument.title || ''))
		},

		set_all_in_category(category, on) {
			const framework_ids = this.frameworks_in_category(category).map(fr => fr.lsdoc_identifier)
			if (on) {
				for (const fid of framework_ids) {
					if (!this.selected_framework_identifiers.includes(fid)) {
						this.selected_framework_identifiers.push(fid)
					}
				}
			} else {
				this.selected_framework_identifiers = this.selected_framework_identifiers.filter(id => !framework_ids.includes(id))
			}
		},

		export_data() {
			if (this.exporting) return
			if (!this.can_export) return
			this.exporting = true
			const payload = {
				custom_export_id: this.selected_export_id,
				framework_identifiers: this.selected_framework_identifiers,
			}
			U.loading_start()
			U.ajax('generate_custom_export_data', payload, result => {
				U.loading_stop()
				this.exporting = false
				if (result.status !== 'ok') {
					const msg = result.status === 'Ajax server error'
						? 'The export request did not complete. This can happen when the export is too large or takes too long. Try selecting fewer frameworks and exporting again.'
						: 'Custom export failed: ' + result.status
					vapp.$alert(msg)
					return
				}
				const rows = result.rows || []
				const warnings = result.warnings || []
				if (rows.length <= 1) {
					let msg = 'No items matched the selected export and frameworks.'
					if (warnings.length) {
						msg += `\n\nAdditionally, ${warnings.length} framework(s) could not be loaded:\n\n` + warnings.join('\n')
					}
					vapp.$alert(msg)
					return
				}
				const date_string = date.format(new Date(), 'YYYY-MM-DD')
				const filename = `satchel-custom-export-${this.selected_export_id}-${date_string}.csv`
				U.download_file(CSV.stringify(rows), filename)
				if (warnings.length) {
					vapp.$alert(`Export complete, but ${warnings.length} framework(s) could not be loaded and were skipped:\n\n` + warnings.join('\n'))
				}
			})
		},
	},
}
</script>

<style lang="scss">
.k-custom-export-title {
	border-bottom: 1px solid #999;
}
.k-custom-export-body {
	font-size: 16px;
	color: #000;

	.v-select__selections .v-chip {
		margin: 6px 6px 4px 0;
	}
}
.k-custom-export-category {
	border: 1px solid #ccc;
}
</style>
